/**
 * Driver scope cache: localStorage with TTL and userId namespace.
 *
 * Cache strategy:
 * - Scope is cached with a TTL (default 15 min, configurable via VITE_SCOPE_CACHE_TTL_MS).
 * - Cache is invalidated when the access token is newer than the cached scope (token iat > scope timestamp).
 * - On 401 + token renewal the API interceptor dispatches SCOPE_INVALIDATED_EVENT; the app refetches and clears storage.
 * - Storage key is namespaced by userId (Auth0 sub) when provided; legacy key "userScope" is used when not.
 *
 * Exported: SCOPE_STORAGE_KEY_PREFIX, getScopeStorageKey, getUserScopeFromStorage, saveUserScopeToStorage,
 * clearUserScopeFromStorage, shouldRefreshScope, SCOPE_INVALIDATED_EVENT, dispatchScopeInvalidated.
 */

import type { OperationShift } from "./operationShifts";

/** Scope shape returned by GET /me/scope (matches DriverContext UserScope). */
export interface DriverScope {
  role: string;
  companies: { id: number; name: string }[];
  operations: { id: number; name: string; companyId: number; shifts?: OperationShift[] | null }[];
  svcs: { id: number; name: string; code: string; operationId: number; availabilityDays: number[]; availabilityShifts?: string[] }[];
}

export interface ScopeStorageData {
  data: DriverScope;
  timestamp: number;
  expiresAt: number;
}

export const SCOPE_STORAGE_KEY_PREFIX = "userScope";

const DEFAULT_SCOPE_CACHE_TTL_MS = 15 * 60 * 1000; // 15 minutes
const MAX_SCOPE_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const MIN_SCOPE_CACHE_TTL_MS = 60 * 1000; // 1 minute

function getScopeCacheDurationMs(): number {
  const raw = import.meta.env.VITE_SCOPE_CACHE_TTL_MS;
  if (raw === undefined || raw === "") return DEFAULT_SCOPE_CACHE_TTL_MS;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < MIN_SCOPE_CACHE_TTL_MS) return DEFAULT_SCOPE_CACHE_TTL_MS;
  return Math.min(n, MAX_SCOPE_CACHE_TTL_MS);
}

export function getScopeStorageKey(userId?: string | null): string {
  if (userId) {
    return `${SCOPE_STORAGE_KEY_PREFIX}_${userId}`;
  }
  return SCOPE_STORAGE_KEY_PREFIX;
}

function isScopeShape(obj: unknown): obj is DriverScope {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "companies" in obj &&
    "operations" in obj &&
    "svcs" in obj
  );
}

function nowOrZero(n: unknown): number {
  return typeof n === "number" && Number.isFinite(n) ? n : 0;
}

/**
 * Reads scope from localStorage. If userId is set and namespaced key is empty,
 * tries to migrate from legacy "userScope" (raw scope or { data, timestamp, expiresAt }).
 */
export function getUserScopeFromStorage(userId?: string | null): DriverScope | null {
  try {
    const key = getScopeStorageKey(userId);
    let stored = localStorage.getItem(key);

    if (userId && !stored) {
      const legacyStored = localStorage.getItem(SCOPE_STORAGE_KEY_PREFIX);
      if (legacyStored) {
        try {
          const parsed = JSON.parse(legacyStored) as ScopeStorageData | DriverScope;
          if (parsed && typeof parsed === "object") {
            if ("data" in parsed && "timestamp" in parsed && "expiresAt" in parsed) {
              const asStorage = parsed as ScopeStorageData;
              if (nowOrZero(asStorage.expiresAt) > Date.now()) {
                localStorage.setItem(key, legacyStored);
                localStorage.removeItem(SCOPE_STORAGE_KEY_PREFIX);
                stored = legacyStored;
              }
            } else if (isScopeShape(parsed)) {
              const now = Date.now();
              const ttl = getScopeCacheDurationMs();
              const wrapped: ScopeStorageData = {
                data: parsed,
                timestamp: now,
                expiresAt: now + ttl,
              };
              localStorage.setItem(key, JSON.stringify(wrapped));
              localStorage.removeItem(SCOPE_STORAGE_KEY_PREFIX);
              return parsed;
            }
          }
        } catch {
          // ignore
        }
      }
    }

    if (!stored) return null;

    const storageData = JSON.parse(stored) as ScopeStorageData;
    const now = Date.now();

    if (now > storageData.expiresAt) {
      clearUserScopeFromStorage(userId);
      return null;
    }

    return storageData.data;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error("Error loading scope from storage:", error);
    }
    return null;
  }
}

export function saveUserScopeToStorage(
  scope: DriverScope,
  userId?: string | null
): void {
  try {
    const key = getScopeStorageKey(userId);
    const now = Date.now();
    const ttl = getScopeCacheDurationMs();
    const storageData: ScopeStorageData = {
      data: scope,
      timestamp: now,
      expiresAt: now + ttl,
    };
    localStorage.setItem(key, JSON.stringify(storageData));
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error("Error saving scope to storage:", error);
    }
  }
}

export function clearUserScopeFromStorage(userId?: string | null): void {
  try {
    localStorage.removeItem(getScopeStorageKey(userId));
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error("Error clearing scope from storage:", error);
    }
  }
}

export function shouldRefreshScope(
  tokenIssuedAtMs: number,
  userId?: string | null
): boolean {
  try {
    const key = getScopeStorageKey(userId);
    const stored = localStorage.getItem(key);
    if (!stored) return true;
    const storageData = JSON.parse(stored) as ScopeStorageData;
    return tokenIssuedAtMs > storageData.timestamp;
  } catch {
    return true;
  }
}

export const SCOPE_INVALIDATED_EVENT = "flashbox:scope-invalidated";

export function dispatchScopeInvalidated(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(SCOPE_INVALIDATED_EVENT));
  }
}
